const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizeTags = (value) => {
  if (Array.isArray(value)) return value.map(normalizeText).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map(normalizeText).filter(Boolean);
  return [];
};

const candidateId = (item = {}) => String(
  item.track_id || item.id || item.youtube_id || item.youtubeId || item.url || '',
);

const overlapCount = (left = [], right = []) => {
  const rightSet = new Set(normalizeTags(right));
  return normalizeTags(left).filter((tag) => rightSet.has(tag)).length;
};

const buildReason = (signals, candidate) => {
  if (signals.sameCollection) return `More from ${candidate.collection_title || candidate.album || 'this collection'}`;
  if (signals.sameArtist) return `Because you are listening to ${candidate.artist || candidate.dj}`;
  if (signals.sameGenre) return `More ${candidate.genre || 'music'} for this vibe`;
  if (signals.sameMood) return `Matches the ${candidate.mood} mood`;
  if (signals.tagOverlap) return 'Similar style and culture tags';
  if (candidate.featured) return 'Featured on Rastyle';
  return 'Keeping the vibe going';
};

const scoreCandidate = (seed = {}, candidate = {}) => {
  const seedArtist = normalizeText(seed.artist || seed.dj);
  const candidateArtist = normalizeText(candidate.artist || candidate.dj);
  const seedGenre = normalizeText(seed.genre);
  const candidateGenre = normalizeText(candidate.genre);
  const seedMood = normalizeText(seed.mood);
  const candidateMood = normalizeText(candidate.mood);
  const seedEra = normalizeText(seed.era);
  const candidateEra = normalizeText(candidate.era);
  const seedCollection = normalizeText(seed.collection_id || seed.mixtape_id || seed.album_id);
  const candidateCollection = normalizeText(candidate.collection_id || candidate.mixtape_id || candidate.album_id);
  const tagOverlap = overlapCount(seed.tags, candidate.tags);

  const signals = {
    sameCollection: Boolean(seedCollection && candidateCollection && seedCollection === candidateCollection),
    sameArtist: Boolean(seedArtist && candidateArtist && seedArtist === candidateArtist),
    sameGenre: Boolean(seedGenre && candidateGenre && seedGenre === candidateGenre),
    sameMood: Boolean(seedMood && candidateMood && seedMood === candidateMood),
    sameEra: Boolean(seedEra && candidateEra && seedEra === candidateEra),
    tagOverlap,
  };

  let score = 0;
  if (signals.sameCollection) score += 50;
  if (signals.sameArtist) score += 40;
  if (signals.sameGenre) score += 35;
  if (signals.sameMood) score += 25;
  if (signals.sameEra) score += 15;
  score += Math.min(tagOverlap * 8, 24);
  if (candidate.featured) score += 5;
  score += Math.min(Number(candidate.popularity || candidate.play_count || 0) / 1000, 10);

  return {
    score,
    reason: buildReason(signals, candidate),
    source: signals.sameCollection
      ? 'collection'
      : signals.sameArtist
        ? 'artist'
        : signals.sameGenre
          ? 'genre'
          : signals.sameMood
            ? 'mood'
            : tagOverlap
              ? 'tags'
              : 'discovery',
  };
};

const rankAutoplayCandidates = ({
  seed,
  candidates = [],
  queue = [],
  history = [],
  limit = 5,
  maxPerArtist = 2,
} = {}) => {
  if (!seed || !Array.isArray(candidates) || limit <= 0) return [];

  const excluded = new Set([
    candidateId(seed),
    ...queue.map(candidateId),
    ...history.slice(-20).map(String),
  ].filter(Boolean));

  const ranked = candidates
    .filter((candidate) => {
      const id = candidateId(candidate);
      if (!id || excluded.has(id)) return false;
      if (candidate.deleted_at) return false;
      if (candidate.status && candidate.status !== 'published') return false;
      if (candidate.visibility && candidate.visibility !== 'public') return false;
      return true;
    })
    .map((candidate) => ({ ...candidate, ...scoreCandidate(seed, candidate) }))
    .sort((left, right) => right.score - left.score || candidateId(left).localeCompare(candidateId(right)));

  const artistCounts = new Map();
  const selected = [];
  for (const candidate of ranked) {
    const artist = normalizeText(candidate.artist || candidate.dj || 'unknown');
    const count = artistCounts.get(artist) || 0;
    if (count >= maxPerArtist) continue;
    artistCounts.set(artist, count + 1);
    selected.push(candidate);
    if (selected.length >= limit) break;
  }
  return selected;
};

export {
  candidateId,
  normalizeTags,
  overlapCount,
  rankAutoplayCandidates,
  scoreCandidate,
};
